import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Spinner, Alert, Pagination, Form, InputGroup, Badge, Modal, Nav, Tab } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FaSearch, FaUserShield, FaUser, FaUserCog, FaLock, FaLockOpen, FaTrashAlt, FaUserTimes } from 'react-icons/fa';
import { useUserAuth } from '../../context/UserAuthContext';
import axios from 'axios';
import './Admin.css';

const API_BASE_URI = process.env.REACT_APP_API_BASE_URI;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletedLoading, setDeletedLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletedError, setDeletedError] = useState('');
  const [activePage, setActivePage] = useState(1);
  const [deletedActivePage, setDeletedActivePage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [deletedTotalPages, setDeletedTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [deletedSearchTerm, setDeletedSearchTerm] = useState('');
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [suspendAction, setSuspendAction] = useState(false);
  const [activeTab, setActiveTab] = useState('active');
  const { user } = useUserAuth();
  const { t } = useTranslation();
  const usersPerPage = 10;

  useEffect(() => {
    if (activeTab === 'active') {
      fetchUsers();
    } else if (activeTab === 'deleted') {
      fetchDeletedUsers();
    }
  }, [activeTab]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');

      const token = await user.getIdToken();
      const headers = { Authorization: `Bearer ${token}` };

      const response = await axios.get(`${API_BASE_URI}/admin/users`, { headers });
      
      setUsers(response.data.users || []);
      
      // Calculate total pages
      const total = response.data.users?.length || 0;
      setTotalPages(Math.ceil(total / usersPerPage));
      
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchDeletedUsers = async () => {
    try {
      setDeletedLoading(true);
      setDeletedError('');

      const token = await user.getIdToken();
      const headers = { Authorization: `Bearer ${token}` };

      const response = await axios.get(`${API_BASE_URI}/admin/users/deleted`, { headers });
      
      setDeletedUsers(response.data.deletedUsers || []);
      
      // Calculate total pages
      const total = response.data.deletedUsers?.length || 0;
      setDeletedTotalPages(Math.ceil(total / usersPerPage));
      
    } catch (err) {
      console.error('Error fetching deleted users:', err);
      setDeletedError('Failed to load deleted users');
    } finally {
      setDeletedLoading(false);
    }
  };

  const handleChangeRole = (user) => {
    setSelectedUser(user);
    setNewRole(user.role || 'user');
    setShowRoleModal(true);
  };

  const handleSaveRole = async () => {
    try {
      const token = await user.getIdToken();
      const headers = { Authorization: `Bearer ${token}` };

      await axios.patch(
        `${API_BASE_URI}/admin/users/${selectedUser.id}/role`, 
        { role: newRole },
        { headers }
      );

      // Update users list
      setUsers(users.map(u => 
        u.id === selectedUser.id ? { ...u, role: newRole } : u
      ));
      
      setShowRoleModal(false);
      
    } catch (err) {
      console.error('Error updating user role:', err);
      setError('Failed to update user role');
    }
  };

  const handleToggleSuspension = (user, suspend) => {
    setSelectedUser(user);
    setSuspendAction(suspend);
    setShowSuspendModal(true);
  };

  const handleSaveSuspension = async () => {
    try {
      const token = await user.getIdToken();
      const headers = { Authorization: `Bearer ${token}` };

      await axios.patch(
        `${API_BASE_URI}/admin/users/${selectedUser.id}/suspension`, 
        { suspend: suspendAction },
        { headers }
      );

      // Update users list
      setUsers(users.map(u => 
        u.id === selectedUser.id ? { ...u, isSuspended: suspendAction } : u
      ));
      
      setShowSuspendModal(false);
      
    } catch (err) {
      console.error('Error updating user suspension:', err);
      setError('Failed to update user suspension status');
    }
  };

  // Filter users based on search term
  const filteredUsers = users.filter(user => 
    user.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.phoneNumber?.includes(searchTerm)
  );

  // Filter deleted users based on search term
  const filteredDeletedUsers = deletedUsers.filter(user => 
    user.displayName?.toLowerCase().includes(deletedSearchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(deletedSearchTerm.toLowerCase()) ||
    user.phoneNumber?.includes(deletedSearchTerm)
  );

  // Get current page of users
  const currentUsers = filteredUsers.slice(
    (activePage - 1) * usersPerPage,
    activePage * usersPerPage
  );

  // Get current page of deleted users
  const currentDeletedUsers = filteredDeletedUsers.slice(
    (deletedActivePage - 1) * usersPerPage,
    deletedActivePage * usersPerPage
  );

  // Create pagination items for active users
  const paginationItems = [];
  for (let number = 1; number <= totalPages; number++) {
    paginationItems.push(
      <Pagination.Item 
        key={number} 
        active={number === activePage}
        onClick={() => setActivePage(number)}
      >
        {number}
      </Pagination.Item>
    );
  }

  // Create pagination items for deleted users
  const deletedPaginationItems = [];
  for (let number = 1; number <= deletedTotalPages; number++) {
    deletedPaginationItems.push(
      <Pagination.Item 
        key={number} 
        active={number === deletedActivePage}
        onClick={() => setDeletedActivePage(number)}
      >
        {number}
      </Pagination.Item>
    );
  }

  const handleTabSelect = (key) => {
    setActiveTab(key);
  };

  return (
    <div className="user-management">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>{t('adminDashboard.userManagement')}</h2>
      </div>

      <Tab.Container activeKey={activeTab} onSelect={handleTabSelect}>
        <Card>
          <Card.Header>
            <Nav variant="tabs">
              <Nav.Item>
                <Nav.Link eventKey="active">{t('adminDashboard.users')}</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="deleted">{t('adminDashboard.deletedUsers') || 'Deleted Users'}</Nav.Link>
              </Nav.Item>
            </Nav>
          </Card.Header>
          <Tab.Content>
            {/* Active Users Tab */}
            <Tab.Pane eventKey="active">
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  {t('adminDashboard.activeUsers') || 'Active Users'}
                  <InputGroup className="search-input">
                    <InputGroup.Text>
                      <FaSearch />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder={t('adminDashboard.searchUsers')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </div>
              </Card.Header>
              <Card.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {loading ? (
                  <div className="text-center py-3">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">{t('loading')}</span>
                    </Spinner>
                  </div>
                ) : currentUsers.length === 0 ? (
                  <Alert variant="info">
                    {searchTerm 
                      ? t('adminDashboard.noUserMatchSearch') 
                      : t('adminDashboard.noUsersYet')
                    }
                  </Alert>
                ) : (
                  <div className="table-responsive">
                    <Table striped hover>
                      <thead>
                        <tr>
                          <th>{t('adminDashboard.name')}</th>
                          <th>{t('adminDashboard.email')}</th>
                          <th>{t('adminDashboard.phone')}</th>
                          <th>{t('adminDashboard.lastLogin')}</th>
                          <th>{t('adminDashboard.status') || 'Status'}</th>
                          <th>{t('adminDashboard.role')}</th>
                          <th>{t('adminDashboard.actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentUsers.map(u => (
                          <tr key={u.id} className={u.isSuspended ? 'suspended-user' : ''}>
                            <td>{u.displayName || t('adminDashboard.noName')}</td>
                            <td>{u.email || t('adminDashboard.noEmail')}</td>
                            <td>{u.phoneNumber || t('adminDashboard.noPhone')}</td>
                            <td>
                              {u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : t('adminDashboard.never')}
                            </td>
                            <td>
                              {u.isSuspended ? (
                                <Badge bg="warning" text="dark">
                                  <FaLock className="me-1" />
                                  {t('adminDashboard.suspended') || 'Suspended'}
                                </Badge>
                              ) : (
                                <Badge bg="success">
                                  <FaLockOpen className="me-1" />
                                  {t('adminDashboard.active') || 'Active'}
                                </Badge>
                              )}
                            </td>
                            <td>
                              {u.role === 'admin' ? (
                                <Badge bg="danger">
                                  <FaUserShield className="me-1" />
                                  {t('adminDashboard.admin')}
                                </Badge>
                              ) : (
                                <Badge bg="secondary">
                                  <FaUser className="me-1" />
                                  {t('adminDashboard.user')}
                                </Badge>
                              )}
                            </td>
                            <td>
                              <div className="d-flex gap-1">
                                <Button 
                                  variant="outline-primary" 
                                  size="sm"
                                  onClick={() => handleChangeRole(u)}
                                  disabled={u.id === user.uid} // Prevent changing own role
                                  title={t('adminDashboard.changeRole')}
                                >
                                  <FaUserCog />
                                </Button>
                                {u.isSuspended ? (
                                  <Button 
                                    variant="outline-success" 
                                    size="sm"
                                    onClick={() => handleToggleSuspension(u, false)}
                                    disabled={u.id === user.uid} // Prevent suspending yourself
                                    title={t('adminDashboard.unsuspendUser') || 'Unsuspend User'}
                                  >
                                    <FaLockOpen />
                                  </Button>
                                ) : (
                                  <Button 
                                    variant="outline-warning" 
                                    size="sm"
                                    onClick={() => handleToggleSuspension(u, true)}
                                    disabled={u.id === user.uid} // Prevent suspending yourself
                                    title={t('adminDashboard.suspendUser') || 'Suspend User'}
                                  >
                                    <FaLock />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
              <Card.Footer>
                <Pagination className="mb-0 justify-content-center">
                  <Pagination.First onClick={() => setActivePage(1)} disabled={activePage === 1} />
                  <Pagination.Prev onClick={() => setActivePage(Math.max(1, activePage - 1))} disabled={activePage === 1} />
                  {paginationItems}
                  <Pagination.Next onClick={() => setActivePage(Math.min(totalPages, activePage + 1))} disabled={activePage === totalPages} />
                  <Pagination.Last onClick={() => setActivePage(totalPages)} disabled={activePage === totalPages} />
                </Pagination>
              </Card.Footer>
            </Tab.Pane>

            {/* Deleted Users Tab */}
            <Tab.Pane eventKey="deleted">
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  {t('adminDashboard.deletedUsers') || 'Deleted Users'}
                  <InputGroup className="search-input">
                    <InputGroup.Text>
                      <FaSearch />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder={t('adminDashboard.searchUsers')}
                      value={deletedSearchTerm}
                      onChange={(e) => setDeletedSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </div>
              </Card.Header>
              <Card.Body>
                {deletedError && <Alert variant="danger">{deletedError}</Alert>}
                {deletedLoading ? (
                  <div className="text-center py-3">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">{t('loading')}</span>
                    </Spinner>
                  </div>
                ) : currentDeletedUsers.length === 0 ? (
                  <Alert variant="info">
                    {deletedSearchTerm 
                      ? t('adminDashboard.noUserMatchSearch') 
                      : t('adminDashboard.noDeletedUsers') || 'No deleted users found'
                    }
                  </Alert>
                ) : (
                  <div className="table-responsive">
                    <Table striped hover>
                      <thead>
                        <tr>
                          <th>{t('adminDashboard.name')}</th>
                          <th>{t('adminDashboard.email')}</th>
                          <th>{t('adminDashboard.phone')}</th>
                          <th>{t('adminDashboard.role')}</th>
                          <th>{t('adminDashboard.deletedAt') || 'Deleted At'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentDeletedUsers.map(u => (
                          <tr key={u.id}>
                            <td>{u.displayName || t('adminDashboard.noName')}</td>
                            <td>{u.email || t('adminDashboard.noEmail')}</td>
                            <td>{u.phoneNumber || t('adminDashboard.noPhone')}</td>
                            <td>
                              {u.role === 'admin' ? (
                                <Badge bg="danger">
                                  <FaUserShield className="me-1" />
                                  {t('adminDashboard.admin')}
                                </Badge>
                              ) : (
                                <Badge bg="secondary">
                                  <FaUser className="me-1" />
                                  {t('adminDashboard.user')}
                                </Badge>
                              )}
                            </td>
                            <td>
                              {u.deletedAt ? new Date(u.deletedAt).toLocaleString() : t('adminDashboard.unknown') || 'Unknown'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
              <Card.Footer>
                <Pagination className="mb-0 justify-content-center">
                  <Pagination.First onClick={() => setDeletedActivePage(1)} disabled={deletedActivePage === 1} />
                  <Pagination.Prev onClick={() => setDeletedActivePage(Math.max(1, deletedActivePage - 1))} disabled={deletedActivePage === 1} />
                  {deletedPaginationItems}
                  <Pagination.Next onClick={() => setDeletedActivePage(Math.min(deletedTotalPages, deletedActivePage + 1))} disabled={deletedActivePage === deletedTotalPages} />
                  <Pagination.Last onClick={() => setDeletedActivePage(deletedTotalPages)} disabled={deletedActivePage === deletedTotalPages} />
                </Pagination>
              </Card.Footer>
            </Tab.Pane>
          </Tab.Content>
        </Card>
      </Tab.Container>

      {/* Role Change Modal */}
      <Modal show={showRoleModal} onHide={() => setShowRoleModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('adminDashboard.changeRole')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>{t('adminDashboard.user')}:</strong> {selectedUser?.displayName || selectedUser?.email}
          </p>
          <Form.Group>
            <Form.Label>{t('adminDashboard.selectRole')}</Form.Label>
            <Form.Select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
            >
              <option value="user">{t('adminDashboard.user')}</option>
              <option value="admin">{t('adminDashboard.admin')}</option>
            </Form.Select>
          </Form.Group>
          <Alert variant="warning" className="mt-3">
            {t('adminDashboard.roleChangeWarning')}
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRoleModal(false)}>
            {t('cancel')}
          </Button>
          <Button variant="primary" onClick={handleSaveRole}>
            {t('save')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Suspension Modal */}
      <Modal show={showSuspendModal} onHide={() => setShowSuspendModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {suspendAction 
              ? t('adminDashboard.suspendUser') || 'Suspend User' 
              : t('adminDashboard.unsuspendUser') || 'Unsuspend User'
            }
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>{t('adminDashboard.user')}:</strong> {selectedUser?.displayName || selectedUser?.email}
          </p>
          {suspendAction ? (
            <Alert variant="warning">
              {t('adminDashboard.suspendWarning') || 'Suspending a user will prevent them from logging in or using the application. Are you sure you want to suspend this user?'}
            </Alert>
          ) : (
            <Alert variant="info">
              {t('adminDashboard.unsuspendInfo') || 'Unsuspending a user will restore their access to the application. Do you want to continue?'}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSuspendModal(false)}>
            {t('cancel')}
          </Button>
          <Button 
            variant={suspendAction ? "warning" : "success"} 
            onClick={handleSaveSuspension}
          >
            {suspendAction 
              ? t('adminDashboard.suspendButton') || 'Suspend User' 
              : t('adminDashboard.unsuspendButton') || 'Unsuspend User'
            }
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UserManagement;